import React, { ReactElement, useEffect, useState } from 'react';
import { Options } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Colours, GraphType } from '@components-clean/central-monitoring/panels/panel-constants';
import { BubbleChart, BubbleSeries } from '@components-clean/core/charts/bubble/bubble-chart';
import { ColumnChart } from '@components-clean/core/charts/column';
import ComboColumnLineChart, {
    HighchartsTooltipElementProps,
} from '@components-clean/core/charts/combo-column-line-chart';
import { DonutChart } from '@components-clean/core/charts/donut/donut-chart';
import { HeatMapChart } from '@components-clean/core/charts/heatmap/heat-map-chart';
import { LineChart } from '@components-clean/core/charts/line/line-chart';
import {
    getHorizontalLines,
    getSeriesData,
    getSparkSeriesData,
    getSubtitle as getLineSubtitle,
    getYValueBounds,
} from '@components-clean/core/charts/line/utils';
import { RadialChart } from '@components-clean/core/charts/radial';
import { IPopoutInfo, SankeyChart } from '@components-clean/core/charts/sankey';
import { ScatterPlot } from '@components-clean/core/charts/scatter-plot/scatter-plot';
import {
    eventRateTooltip,
    funnelThresholdSeries,
    funnelTooltip,
    funnelTrendTooltip,
    getEventRateSubtitle,
    getFunnelSubtitle,
    getFunnelTrendSubtitle,
    getPropOTSubtitle,
    getScatterPlotBounds,
    getTRIOpDateSubtitle,
    getTRIOpSubtitle,
    getTriOpTrendSubtitle,
    mapEventRatesToScatterPlot,
    mapFunnelToScatter,
    mapFunnelTrendToScatter,
    mapPropOTToScatter,
    mapRateGroupToScatter,
    mapRateGroupTrendToScatter,
    mapTRIOpDateToScatter,
    mapTRIOpToScatter,
    mapTRIOpTrendToScatter,
    propOTTooltip,
    rateGroupTooltip,
    rateGroupTrendTooltip,
    ScatterThresholdData,
    triOpTooltip,
    triOpTrendTooltip,
} from '@components-clean/core/charts/scatter-plot/utils';
import { StackedBarChart, StackedColumnChart } from '@components-clean/core/charts/stacked';
import { InfoField } from '@components-clean/core/info-field';
import {
    ChartHorizontalLine,
    Column,
    ComboColumnLine,
    DataSource,
    EventRatesData,
    FunnelData,
    FunnelTrendData,
    LineResult,
    ModelConfig,
    PanelData,
    ProportionsOTData,
    RadialChartData,
    RateGroupData,
    RateGroupTrendData,
    SankeyChartData,
    SeriesConfig,
    SeriesDataDonut,
    SeriesDataHeatMap,
    SeriesDataHeatMapV2,
    SparkLineSeriesData,
    StackedChartData,
    TRIOpDateData,
    TRIOpSeriesData,
    TRIOpTrendData,
} from '@interfaces-clean/panels.interface';
import {
    PanelAverageLineThresholdType,
    PanelDisplayUnits,
    PanelIntervalType,
    PanelModelType,
    PanelSeriesType,
    PanelTheme,
    PanelType,
} from 'types/graphql-global-types';
import { useLocalState, useTranslation } from '@hooks/index';

interface PanelDisplayProps {
    type: PanelType;
    chartConfig?: {
        type?: GraphType;
        xAxisTitle?: string;
        yAxisTitle?: string;
        plotTitle?: string;
        thresholdType?: PanelAverageLineThresholdType | null;
        seriesConfig?: SeriesConfig;
        displayUnits?: PanelDisplayUnits | null;
        showxAxisLabels?: boolean;
        customOptions?: Options;
    };
    modelConfig?: ModelConfig;
    panelData: PanelData;
    hidePlotTitle?: boolean;
    setLoadingComplete?: () => void;
    panelFunctions?: () => void;
    entityName?: string;
    siteId?: number;
    hideLegend?: boolean;
    previewMode?: boolean;
    dashboardType?: string;
    showLegend?: boolean;
    TooltipElement?: (props: HighchartsTooltipElementProps) => ReactElement;
    theme?: PanelTheme;
    isInactive?: boolean;
    dataSource?: DataSource;
    showSubTitle: boolean;
    showWaterMark?: boolean;
    dataviewRef?: string | null;
    chartRef?: React.RefObject<HighchartsReact.RefObject | null>;
    popoutInfo?: IPopoutInfo;
    exportFilename?: string;
    version: number;
    isPreview?: boolean;
    customEmptyPanelMessage?: string;
    customHeight?: number;
}

interface EmptyPanelProps {
    message?: string;
    isPreview?: boolean;
}

const EmptyPanel = ({ message, isPreview = false }: EmptyPanelProps) => {
    const { t } = useTranslation('cm');

    // In case of legacy panel preview dialog, we are displaying plain text so checking the prop "isPreview" here.
    // Event rates, tri ops date and proportions ot, they are new panels, and we always display the InfoField component.
    // So not sending isPreview prop for these new panels.
    return !isPreview ? (
        <InfoField message={message ?? t('componentsClean.panels.noData')} type="Info" layout="center" />
    ) : (
        <span> {t('componentsClean.panels.noDataForPreview')}</span>
    );
};

const PanelDisplay = (panelProps: PanelDisplayProps) => {
    const { t } = useTranslation('cm');
    const [panel, setPanel] = useState<PanelDisplayProps | null>(null);
    const localState = useLocalState();

    useEffect(() => {
        if (!panel) {
            setPanel(panelProps);
            return;
        }
        const { chartRef, ...props } = panelProps;
        const { chartRef: stateRef, ...panelState } = panel;

        if (JSON.stringify(props) !== JSON.stringify(panelState)) setPanel(panelProps);
    }, [panelProps]);

    const { setLoadingComplete, theme, version, isPreview, customEmptyPanelMessage } = panelProps;

    if (customEmptyPanelMessage) {
        return <EmptyPanel message={customEmptyPanelMessage} isPreview={isPreview} />;
    }

    if (panel === null) {
        if (setLoadingComplete) setLoadingComplete();
        return null;
    }

    let horizontalLines: ChartHorizontalLine[] = [];
    let threshold1: ScatterThresholdData | undefined;
    let threshold2: ScatterThresholdData | undefined;

    const { type, panelData, showLegend, chartConfig, showWaterMark, dataviewRef, chartRef, popoutInfo } = panel;

    if (type === PanelType.GRAPH) {
        switch (chartConfig?.type) {
            case 'SANKEY': {
                const { TooltipElement, exportFilename } = panel;
                const sankeyChartData = panelData as SankeyChartData;

                if (!sankeyChartData.data) {
                    return <EmptyPanel isPreview={isPreview} />;
                }

                return (
                    <SankeyChart
                        chartRef={chartRef}
                        panelData={sankeyChartData}
                        TooltipElement={TooltipElement}
                        popoutInfo={popoutInfo}
                        exportFilename={exportFilename}
                    />
                );
            }
            case 'RADIAL': {
                const radialChartData = panelData as RadialChartData;

                if (radialChartData.series.length === 0) {
                    return <EmptyPanel isPreview={isPreview} />;
                }

                return (
                    <RadialChart
                        chartRef={chartRef}
                        panelData={radialChartData}
                        showLegend={showLegend}
                        theme={theme}
                        title={chartConfig.plotTitle}
                    />
                );
            }
            case 'STACKEDBAR': {
                const stackedChartData = panelData as StackedChartData;

                if (!stackedChartData.series || stackedChartData.series?.length === 0) {
                    return <EmptyPanel isPreview={isPreview} />;
                }

                return (
                    <div className="k-h-full k-display-flex k-align-items-center">
                        <div className="k-w-full">
                            <StackedBarChart
                                chartRef={chartRef}
                                panelData={stackedChartData}
                                showLegend={showLegend}
                                theme={theme}
                                xAxisTitle={panel.chartConfig?.xAxisTitle}
                                yAxisTitle={panel.chartConfig?.yAxisTitle}
                                showWaterMark={showWaterMark}
                                title={chartConfig.plotTitle}
                                customOptions={chartConfig.customOptions}
                                customHeight={panel.customHeight}
                            />
                        </div>
                    </div>
                );
            }

            case 'STACKEDCOLUMN': {
                const stackedChartData = panelData as StackedChartData;

                if (!stackedChartData.series || stackedChartData.series?.length === 0) {
                    return <EmptyPanel isPreview={isPreview} />;
                }

                return (
                    <StackedColumnChart
                        chartRef={chartRef}
                        panelData={stackedChartData}
                        showLegend={showLegend}
                        theme={theme}
                        xAxisTitle={panel.chartConfig?.xAxisTitle}
                        yAxisTitle={panel.chartConfig?.yAxisTitle}
                        showWaterMark={showWaterMark}
                        title={chartConfig.plotTitle}
                        usePercentStacking={
                            (chartConfig?.customOptions as { usePercentStacking?: boolean })?.usePercentStacking
                        }
                        customOptions={chartConfig.customOptions}
                    />
                );
            }
            case 'COMBOCOLUMNLINE': {
                const { TooltipElement } = panel;

                const comboColumnLineData = panelData as ComboColumnLine;

                if (comboColumnLineData.series.length === 0) {
                    return <EmptyPanel isPreview={isPreview} />;
                }

                return (
                    <ComboColumnLineChart
                        chartRef={chartRef}
                        panelData={comboColumnLineData}
                        showLegend={showLegend}
                        TooltipElement={TooltipElement}
                        dataviewRef={dataviewRef}
                    />
                );
            }
            case 'DONUT': {
                const donutData = panelData as SeriesDataDonut[];

                if (donutData.length === 0) {
                    return <EmptyPanel isPreview={isPreview} />;
                }

                return (
                    <div className="k-h-full k-display-flex k-align-items-center">
                        <div className="k-w-full">
                            <DonutChart
                                chartRef={chartRef}
                                panelData={donutData}
                                showLegend={showLegend}
                                theme={theme}
                                showWaterMark={showWaterMark}
                                title={chartConfig.plotTitle}
                                customOptions={chartConfig.customOptions}
                                customHeight={panel.customHeight}
                            />
                        </div>
                    </div>
                );
            }
            case 'HEATMAP': {
                const heatmapData =
                    version === 1 ? (panelData as SeriesDataHeatMap) : (panelData as SeriesDataHeatMapV2);

                if (version === 1 && Object.keys((heatmapData as SeriesDataHeatMap).heatmapForIds).length === 0) {
                    return <EmptyPanel isPreview={isPreview} />;
                }
                if (version > 1 && !(heatmapData as SeriesDataHeatMapV2).data?.length) {
                    return <EmptyPanel isPreview={isPreview} />;
                }

                return (
                    <HeatMapChart
                        chartRef={chartRef}
                        series={heatmapData}
                        showLegend={showLegend}
                        xAxisTitle={panel.chartConfig?.xAxisTitle}
                        yAxisTitle={panel.chartConfig?.yAxisTitle}
                        plotTitle={panel.chartConfig?.plotTitle}
                        hidePlotTitle={panel.hidePlotTitle}
                        showWaterMark={showWaterMark}
                        version={version}
                    />
                );
            }
            case 'COLUMN': {
                const columnData = panelData as Column;
                if (columnData.studyData.length === 0) {
                    return <EmptyPanel isPreview={isPreview} />;
                }

                return (
                    <ColumnChart
                        chartRef={chartRef}
                        panelData={columnData}
                        showLegend={showLegend}
                        theme={theme}
                        showNumSubjects={columnData.isSortFieldPercentPopulation}
                        showWaterMark={showWaterMark}
                        title={chartConfig.plotTitle}
                    />
                );
            }
            case 'BUBBLE': {
                const bubbleData = panelData as BubbleSeries[];

                if (!bubbleData || (Array.isArray(bubbleData) && bubbleData.length === 0)) {
                    return <EmptyPanel isPreview={isPreview} />;
                }

                return (
                    <div className="k-h-full k-display-flex k-align-items-center">
                        <div className="k-w-full">
                            <BubbleChart
                                chartRef={chartRef}
                                panelData={bubbleData}
                                xAxisTitle={panel.chartConfig?.xAxisTitle}
                                yAxisTitle={panel.chartConfig?.yAxisTitle}
                                showLegend={showLegend}
                                theme={theme}
                                showWaterMark={showWaterMark}
                                title={chartConfig?.plotTitle}
                                customOptions={chartConfig?.customOptions}
                                customHeight={panel.customHeight}
                            />
                        </div>
                    </div>
                );
            }

            case 'SCATTERPLOTLINE': {
                const lineData = panelData as LineResult;

                const seriesData = getSeriesData(lineData, chartConfig.seriesConfig);
                const hasData = !!seriesData.find((s) => s.data.length > 0);
                if (!hasData || !chartConfig.seriesConfig?.type) return <EmptyPanel isPreview={isPreview} />;

                const subTitle = getLineSubtitle(
                    chartConfig.seriesConfig,
                    lineData.threshold1,
                    lineData.threshold2,
                    chartConfig.thresholdType,
                );
                horizontalLines = getHorizontalLines(
                    chartConfig.seriesConfig,
                    lineData.threshold1,
                    lineData.threshold2,
                );

                const minThreshold = lineData.threshold1;
                let maxThreshold = lineData.threshold2;
                if (chartConfig.seriesConfig?.type === PanelSeriesType.SITE_CUMULATIVE) {
                    maxThreshold = horizontalLines.reduce((max, line) => Math.max(max, line.y), 0);
                }

                const [minY, maxY] = getYValueBounds(seriesData, minThreshold, maxThreshold);

                return (
                    <LineChart
                        chartRef={chartRef}
                        showSubtitle={panel.showSubTitle}
                        panelData={seriesData}
                        categories={lineData.lineSubjectByVisitCategories ?? undefined}
                        horizontalLines={horizontalLines}
                        subTitle={subTitle}
                        xTitle={chartConfig.xAxisTitle}
                        yTitle={chartConfig.yAxisTitle}
                        title={chartConfig.plotTitle}
                        theme={theme}
                        seriesType={chartConfig.seriesConfig.type}
                        isDateRange={chartConfig.seriesConfig.type !== PanelSeriesType.SUBJECT_BY_VISIT}
                        showLegend={showLegend}
                        showxAxisLabels={chartConfig.showxAxisLabels}
                        customOptions={chartConfig.customOptions}
                        showWaterMark={showWaterMark}
                        minY={minY}
                        maxY={maxY}
                    />
                );
            }
            case 'SPARKLINE': {
                const lineData = panelData as SparkLineSeriesData;
                const seriesData = getSparkSeriesData(lineData);
                const hasData = !!seriesData.find((s) => s.data.length > 0);

                if (!hasData || !chartConfig.seriesConfig?.type) return <EmptyPanel isPreview={isPreview} />;

                return (
                    <LineChart
                        chartRef={chartRef}
                        showSubtitle={panel.showSubTitle}
                        panelData={seriesData}
                        categories={lineData.categories ?? undefined}
                        xTitle={chartConfig.xAxisTitle}
                        yTitle={chartConfig.yAxisTitle}
                        title={chartConfig.plotTitle}
                        theme={theme}
                        seriesType={chartConfig.seriesConfig.type}
                        isDateRange={chartConfig.seriesConfig.type !== PanelSeriesType.SUBJECT_BY_VISIT}
                        showLegend={showLegend}
                        showxAxisLabels={chartConfig.showxAxisLabels}
                        customOptions={chartConfig.customOptions}
                        showWaterMark={showWaterMark}
                        showCrosshairs={false}
                    />
                );
            }
            default: {
                if (setLoadingComplete) setLoadingComplete();
                return (
                    <h3 className="error-message">
                        {panel.chartConfig?.type} {t('componentsClean.panels.chartNotAvailable')}
                    </h3>
                );
            }
        }
    } else if (type === PanelType.MODEL) {
        // @ts-ignore
        switch (panel.modelConfig?.type) {
            case PanelModelType.TRI_OPERATIONAL:
                const seriesData = panelData as TRIOpSeriesData;
                const scatterPlots = mapTRIOpToScatter(
                    seriesData.points ?? [],
                    panel.modelConfig.stableValue,
                    panel.modelConfig.stableVariableType,
                );

                if (!scatterPlots.find((p) => p.data.length > 0)) return <EmptyPanel isPreview={isPreview} />;

                if (seriesData.threshold1 != null) {
                    horizontalLines.push({
                        color: Colours.AMBER,
                        style: 'Dash',
                        y: seriesData.threshold1,
                    });
                    threshold1 = { value: seriesData.threshold1, type: panel.modelConfig?.threshold1Type };
                }

                if (seriesData.threshold2 != null) {
                    horizontalLines.push({
                        color: Colours.RED,
                        style: 'Dash',
                        y: seriesData.threshold2,
                    });
                    threshold2 = { value: seriesData.threshold2, type: panel.modelConfig?.threshold2Type };
                }

                return (
                    <ScatterPlot
                        horizontalLines={horizontalLines}
                        chartRef={chartRef}
                        showSubtitle={panel.showSubTitle}
                        panelData={scatterPlots}
                        subTitle={getTRIOpSubtitle(threshold1, threshold2, true)}
                        xTitle={panel?.chartConfig?.xAxisTitle}
                        yTitle={panel?.chartConfig?.yAxisTitle}
                        theme={theme}
                        showLegend={showLegend}
                        legendItemWidth={150}
                        legendWidth={450}
                        tooltipFormatter={triOpTooltip}
                        showWaterMark={showWaterMark}
                        title={chartConfig?.plotTitle}
                    />
                );
            case PanelModelType.TRI_OPERATIONAL_TREND:
                const triOpTrendData = panelData as TRIOpTrendData;

                const trendSeriesData = mapTRIOpTrendToScatter(triOpTrendData, panel?.modelConfig?.studyComparison);
                if (triOpTrendData.threshold1 != null) {
                    horizontalLines.push({
                        color: Colours.AMBER,
                        style: 'Dash',
                        y: triOpTrendData.threshold1,
                    });
                    threshold1 = { value: triOpTrendData.threshold1, type: panel.modelConfig?.threshold1Type };
                }

                if (triOpTrendData.threshold2 != null) {
                    horizontalLines.push({
                        color: Colours.RED,
                        style: 'Dash',
                        y: triOpTrendData.threshold2,
                    });
                    threshold2 = { value: triOpTrendData.threshold2, type: panel.modelConfig?.threshold2Type };
                }

                if (trendSeriesData.length === 0) {
                    return <EmptyPanel isPreview={isPreview} />;
                }

                return (
                    <ScatterPlot
                        xTitle={panel?.chartConfig?.xAxisTitle}
                        yTitle={panel?.chartConfig?.yAxisTitle}
                        panelData={trendSeriesData}
                        chartRef={chartRef}
                        tooltipFormatter={triOpTrendTooltip}
                        horizontalLines={horizontalLines}
                        subTitle={getTriOpTrendSubtitle(threshold1, threshold2)}
                        showLegend={showLegend}
                        showSubtitle={panel?.showSubTitle}
                        title={chartConfig?.plotTitle}
                        showWaterMark={showWaterMark}
                    />
                );
            case PanelModelType.RATE_GROUP:
                const rateGroupData = panelData as RateGroupData;
                const rateGroupPlots = mapRateGroupToScatter(
                    rateGroupData.points ?? [],
                    panel.modelConfig?.siteScoring,
                );

                if (!rateGroupPlots.find((p) => p.data.length > 0)) return <EmptyPanel isPreview={isPreview} />;

                return (
                    <ScatterPlot
                        chartRef={chartRef}
                        panelData={rateGroupPlots}
                        xTitle={panel?.chartConfig?.xAxisTitle}
                        yTitle={panel?.chartConfig?.yAxisTitle}
                        theme={theme}
                        showLegend={showLegend}
                        tooltipFormatter={rateGroupTooltip}
                        showWaterMark={showWaterMark}
                        title={chartConfig?.plotTitle}
                    />
                );
            case PanelModelType.RATE_GROUP_TREND:
                const rateGroupTrendData = panelData as RateGroupTrendData;
                const rateGroupTrendSeries = mapRateGroupTrendToScatter(
                    rateGroupTrendData,
                    localState?.state?.selectedStudy?.studyNumber,
                );

                if (rateGroupTrendSeries[0].data.length === 0) {
                    return <EmptyPanel isPreview={isPreview} />;
                }

                return (
                    <ScatterPlot
                        xTitle={panel?.chartConfig?.xAxisTitle}
                        yTitle={panel?.chartConfig?.yAxisTitle}
                        panelData={rateGroupTrendSeries}
                        chartRef={chartRef}
                        tooltipFormatter={rateGroupTrendTooltip}
                        subTitle=""
                        showLegend={showLegend}
                        showSubtitle={panel?.showSubTitle}
                        isDateRange
                        title={chartConfig?.plotTitle}
                        showWaterMark={showWaterMark}
                    />
                );
            case PanelModelType.FUNNEL:
                const funnelData = panelData as FunnelData;
                const funnelPlots = mapFunnelToScatter(funnelData.points ?? []);
                if (!funnelPlots.find((p) => p.data.length > 0)) return <EmptyPanel isPreview={isPreview} />;
                const thresholdSeries = funnelThresholdSeries(
                    funnelData,
                    panel.modelConfig.confidenceIntervalType,
                    panel.modelConfig.hideLower,
                    panel.modelConfig.hideUpper,
                );
                const horizontalLine: ChartHorizontalLine[] = [];
                if (
                    panel.modelConfig.confidenceIntervalType === PanelIntervalType.APPROXIMATE &&
                    funnelData.benchmark !== undefined
                )
                    horizontalLine.push({ y: funnelData.benchmark, color: 'black', style: 'Dash' });

                return (
                    <ScatterPlot
                        chartRef={chartRef}
                        panelData={funnelPlots}
                        showSubtitle={panel.showSubTitle}
                        xTitle={panel?.chartConfig?.xAxisTitle}
                        yTitle={panel?.chartConfig?.yAxisTitle}
                        theme={theme}
                        showLegend={showLegend}
                        tooltipFormatter={funnelTooltip}
                        thresholdSeries={thresholdSeries}
                        horizontalLines={horizontalLine}
                        subTitle={getFunnelSubtitle(
                            panel.modelConfig.benchmarkType,
                            funnelData.benchmark,
                            panel.modelConfig.hideLower,
                            panel.modelConfig.hideUpper,
                            panel.modelConfig.lowerAmber,
                            panel.modelConfig.upperAmber,
                            panel.modelConfig.lowerRed,
                            panel.modelConfig.upperRed,
                        )}
                        showWaterMark={showWaterMark}
                        title={chartConfig?.plotTitle}
                    />
                );
            case PanelModelType.FUNNEL_TREND:
                const funnelTrendData = panelData as FunnelTrendData;

                const { trendLine: funnelTrendPlot, thresholds: trendThresholdSeries } = mapFunnelTrendToScatter(
                    funnelTrendData,
                    panel.modelConfig.confidenceIntervalType,
                    localState?.state?.selectedStudy?.studyNumber,
                );

                if (funnelTrendPlot.data.length === 0) {
                    return <EmptyPanel isPreview={isPreview} />;
                }

                const isExactConfidence: boolean = panel.modelConfig.confidenceIntervalType === PanelIntervalType.EXACT;

                const benchmark =
                    funnelTrendData.studyBenchmark === undefined || funnelTrendData.studyBenchmark === null
                        ? funnelTrendData.benchmark
                        : funnelTrendData.studyBenchmark;

                const trendHorizontalLine: ChartHorizontalLine[] = [];
                if (panel.modelConfig.confidenceIntervalType === PanelIntervalType.APPROXIMATE) {
                    trendHorizontalLine.push({
                        y: benchmark,
                        color: 'black',
                        style: 'Dash',
                    } as ChartHorizontalLine);
                }

                return (
                    <ScatterPlot
                        panelData={[funnelTrendPlot]}
                        chartRef={chartRef}
                        xTitle={panel?.chartConfig?.xAxisTitle}
                        yTitle={panel?.chartConfig?.yAxisTitle}
                        tooltipFormatter={funnelTrendTooltip}
                        thresholdSeries={trendThresholdSeries}
                        horizontalLines={trendHorizontalLine}
                        subTitle={getFunnelTrendSubtitle(
                            panel.modelConfig.benchmarkType,
                            benchmark,
                            panel.modelConfig.hideLower,
                            panel.modelConfig.hideUpper,
                            panel.modelConfig.lowerAmber,
                            panel.modelConfig.upperAmber,
                            panel.modelConfig.lowerRed,
                            panel.modelConfig.upperRed,
                            funnelTrendData.benchmark != null,
                        )}
                        showLegend={showLegend}
                        showSubtitle={panel.showSubTitle}
                        title={chartConfig?.plotTitle}
                        showWaterMark={showWaterMark}
                        minYOverride={isExactConfidence ? undefined : -1}
                        maxYOverride={isExactConfidence ? undefined : 1}
                    />
                );

            default:
                if (setLoadingComplete) setLoadingComplete();
                return (
                    <h3 className="error-message">
                        {t('componentsClean.panels.invalidPanel')} {panel.type}
                    </h3>
                );
        }
    } else if (type === PanelType.TRI_OP_DATE) {
        const triOpDateData = panelData as TRIOpDateData;
        const triOpDateScatter = mapTRIOpDateToScatter(
            triOpDateData.points ?? [],
            panel.modelConfig?.stableValue,
            panel.modelConfig?.stableVariableType,
        );

        if (!triOpDateScatter.find((p) => p.data.length > 0)) return <EmptyPanel />;

        if (triOpDateData.amberThresholdValue != null) {
            horizontalLines.push({
                color: Colours.AMBER,
                style: 'Dash',
                y: triOpDateData.amberThresholdValue,
            });
        }

        if (triOpDateData.redThresholdValue != null) {
            horizontalLines.push({
                color: Colours.RED,
                style: 'Dash',
                y: triOpDateData.redThresholdValue,
            });
        }

        const [minY, maxY] = getScatterPlotBounds(
            triOpDateScatter,
            triOpDateData.amberThresholdValue ?? undefined,
            triOpDateData.redThresholdValue ?? undefined,
        );

        return (
            <ScatterPlot
                horizontalLines={horizontalLines}
                chartRef={chartRef}
                showSubtitle={panel.showSubTitle}
                panelData={triOpDateScatter}
                subTitle={getTRIOpDateSubtitle(
                    triOpDateData.amberThresholdValue ?? undefined,
                    triOpDateData.redThresholdValue ?? undefined,
                    panel.modelConfig?.thresholdCalculation,
                    triOpDateData.modelRate,
                )}
                xTitle={panel?.chartConfig?.xAxisTitle}
                yTitle={panel?.chartConfig?.yAxisTitle}
                theme={theme}
                showLegend={showLegend}
                legendItemWidth={150}
                legendWidth={600} // This will cause problems, speak with product to figure out design
                tooltipFormatter={triOpTooltip}
                showWaterMark={showWaterMark}
                title={chartConfig?.plotTitle}
                minYOverride={minY}
                maxYOverride={maxY}
                scrollLegend
            />
        );
    } else if (type === PanelType.PROPORTIONS_OVER_TIME) {
        const propOTData = panelData as ProportionsOTData;
        let studyName = localState?.state?.selectedStudy?.studyNumber;

        // this can either be at study or country level
        let benchmarkValue = propOTData.study?.benchmarkValue;

        // if there is only one country, display country instead of study data
        if (propOTData.countries != null && Object.keys(propOTData.countries).length === 1) {
            const [countryName] = Object.keys(propOTData.countries);
            benchmarkValue = propOTData.countries[countryName].benchmarkValue;
            studyName = countryName;
        }

        const { scatterPlotData, thresholds, plotBands } = mapPropOTToScatter(
            propOTData.study?.points?.length
                ? propOTData.study?.points
                : propOTData.countries?.[studyName || '']?.points,
            benchmarkValue ?? null,
            studyName,
        );

        if (scatterPlotData.length === 0) return <EmptyPanel />;

        return (
            <ScatterPlot
                isDateRange
                panelData={scatterPlotData}
                chartRef={chartRef}
                xTitle={panel?.chartConfig?.xAxisTitle}
                yTitle={panel?.chartConfig?.yAxisTitle}
                title={chartConfig?.plotTitle}
                showSubtitle={panel.showSubTitle}
                showLegend={showLegend}
                showWaterMark={showWaterMark}
                theme={theme}
                minYOverride={0}
                plotBands={plotBands}
                tooltipFormatter={propOTTooltip(
                    panel.chartConfig?.yAxisTitle,
                    panel.modelConfig?.numeratorName,
                    panel.modelConfig?.denominatorName,
                )}
                subTitle={getPropOTSubtitle(
                    panel.modelConfig?.proportionsOTBenchmarkType,
                    benchmarkValue,
                    panel.modelConfig?.proportionsOTThresholdType,
                    panel.modelConfig?.plotCalculation,
                    panel.modelConfig?.plotBy,
                    panel.modelConfig?.upperAmber,
                    panel.modelConfig?.lowerAmber,
                    panel.modelConfig?.upperRed,
                    panel.modelConfig?.lowerRed,
                    panel.modelConfig?.minSubjects,
                )}
                thresholdSeries={thresholds}
                xAxisFormat="{value:%d-%b-%Y}"
            />
        );
    } else if (type === PanelType.EVENT_RATES_OVER_TIME) {
        const eventRateData = panelData as EventRatesData;
        const { scatterPlotData, thresholds, plotBands } = mapEventRatesToScatterPlot(
            eventRateData,
            localState?.state?.selectedStudy?.studyNumber,
        );

        let studyOrCountryBenchmark = eventRateData.studyBenchmark;
        if (eventRateData.countryData != null && Object.keys(eventRateData.countryData).length === 1) {
            const [countryName] = Object.keys(eventRateData.countryData);
            studyOrCountryBenchmark = eventRateData.countryData[countryName].benchmark;

            if (eventRateData.countryData[countryName].points?.length === 0)
                return <EmptyPanel isPreview={isPreview} />;
        } else if (eventRateData.studyPoints?.length === 0) return <EmptyPanel />;

        const benchmarkLine: ChartHorizontalLine[] = [
            {
                color: Colours.BLACK,
                style: 'Dash',
                y: studyOrCountryBenchmark!,
            },
        ];

        return (
            <ScatterPlot
                chartRef={chartRef}
                showSubtitle={panel.showSubTitle}
                panelData={scatterPlotData}
                tooltipFormatter={eventRateTooltip(
                    panel.chartConfig?.yAxisTitle,
                    panel.modelConfig?.numeratorName,
                    panel.modelConfig?.denominatorName,
                )}
                subTitle={getEventRateSubtitle(
                    panel.modelConfig?.eventRateBenchmarkType,
                    studyOrCountryBenchmark,
                    panel.modelConfig?.eventRateThresholdType,
                    panel.modelConfig?.plotCalculation,
                    panel.modelConfig?.plotBy,
                    panel.modelConfig?.upperAmber,
                    panel.modelConfig?.lowerAmber,
                    panel.modelConfig?.upperRed,
                    panel.modelConfig?.lowerRed,
                    panel.modelConfig?.minSubjects,
                    panel.modelConfig?.minTimeOnStudy,
                )}
                xTitle={panel?.chartConfig?.xAxisTitle}
                yTitle={panel?.chartConfig?.yAxisTitle}
                showLegend={showLegend}
                theme={theme}
                showWaterMark={showWaterMark}
                isDateRange
                thresholdSeries={thresholds}
                plotBands={plotBands}
                horizontalLines={benchmarkLine}
                xAxisFormat="{value:%d-%b-%Y}"
                title={chartConfig?.plotTitle}
            />
        );
    } else {
        if (setLoadingComplete) setLoadingComplete();
        return (
            <h3 className="error-message">
                {t('componentsClean.panels.invalidPanel')} {panel.type}
            </h3>
        );
    }
};

export default PanelDisplay;
